import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddCircleOutline,
  ArrowBack,
  ContentCopyOutlined,
  DeleteOutline,
  ErrorOutline,
  EventRepeatOutlined,
  SaveOutlined,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import ConnString from "../../../ConnString.js";

const apiRoot = ConnString.connstring;
const PERIODS = ["Daily", "Weekly", "Bi-Weekly", "Monthly", "Bi-Monthly", "Quarterly", "Semi Annual", "Annually"];
const amountPattern = /^-?\d*(\.\d{0,2})?$/;
const localDateKey = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};
const today = () => localDateKey();
const dateOnly = (value) => String(value || "").slice(0, 10);
const money = (value) => Number(value || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const codeOf = (branch) => String(branch?.Branch || branch?.BranchCode || branch?.id || branch?.Code || "");
const branchName = (branch) => String(branch?.LocName || branch?.BranchName || branch?.Name || codeOf(branch));
const lineKey = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const emptyLine = (location = "") => ({ _key: lineKey(), AccountCode: "", Title: "", Control: false, Agent: "", Debit: 0, Credit: 0, Location: location });

const defaultStartDate = (bookingDate) => {
  const value = dateOnly(bookingDate);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return today();
  const [year, month] = value.split("-").map(Number);
  const monthEnd = localDateKey(new Date(year, month, 0));
  return monthEnd > today() ? today() : monthEnd;
};

const TransRecurringJV = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestedMode = String(params.get("mode") || "new").toLowerCase();
  const recid = params.get("recid");
  const compID = sessionStorage.getItem("compID");
  const userID = sessionStorage.getItem("userID");
  const view = requestedMode === "view";
  const editable = !view;

  const [mode, setMode] = useState(requestedMode);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [focusedAmount, setFocusedAmount] = useState("");
  const [options, setOptions] = useState({ branches: [], accounts: [], agents: [], bookingDate: "" });
  const [header, setHeader] = useState({
    JVCode: "",
    JVDescription: "",
    DateStart: today(),
    Cycle: "",
    Period: "Monthly",
    Particulars: "",
    DefaultBranch: params.get("branch") || "",
    Status: "Pending",
  });
  const [lines, setLines] = useState([]);

  const post = async (path, body) => {
    const response = await fetch(`${apiRoot}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const raw = await response.text();
    let data;
    try { data = JSON.parse(raw); } catch { throw new Error("The server returned an invalid response."); }
    if (!response.ok || data.success === false) throw new Error(data.message || "Request failed.");
    return data;
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const data = await post("/api/recurring-jv/form-options", { compID, userID });
        if (!active) return;
        setOptions(data);
        const requestedBranch = params.get("branch") || "";
        const defaultBranch = data.branches.some((branch) => codeOf(branch) === requestedBranch)
          ? requestedBranch
          : codeOf(data.branches[0]);

        if (recid) {
          const loaded = await post("/api/recurring-jv/load", { compID, userID, recid, mode: requestedMode, branch: defaultBranch });
          if (!active) return;
          setHeader({
            ...loaded.header,
            DateStart: dateOnly(loaded.header.DateStart),
            DefaultBranch: loaded.header.DefaultBranch || defaultBranch,
          });
          setLines((loaded.details || []).map((line) => ({ ...line, _key: lineKey() })));
          setMode(loaded.mode);
        } else {
          setHeader((current) => ({ ...current, DefaultBranch: defaultBranch, DateStart: defaultStartDate(data.bookingDate) }));
        }
      } catch (error) {
        if (active) setNotice({ severity: "error", text: error.message });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compID, recid, requestedMode, userID]);

  const totals = useMemo(() => lines.reduce((summary, line) => ({
    debit: summary.debit + Number(line.Debit || 0),
    credit: summary.credit + Number(line.Credit || 0),
  }), { debit: 0, credit: 0 }), [lines]);
  const variance = totals.debit - totals.credit;
  const accountFor = (line) => options.accounts.find((account) => String(account.AccountCode) === String(line.AccountCode)) || null;
  const agentFor = (line) => options.agents.find((agent) => String(agent.AgentCode) === String(line.Agent)) || null;
  const updateLine = (index, patch) => setLines((current) => current.map((line, lineIndex) => lineIndex === index ? { ...line, ...patch } : line));

  const validate = () => {
    if (!header.JVCode.trim()) return "Recurring JV code is required.";
    if (!header.JVDescription.trim()) return "Recurring JV description is required.";
    if (!header.DateStart || !header.Period) return "Start date and period are required.";
    if (!Number.isInteger(Number(header.Cycle)) || Number(header.Cycle) <= 0) return "Number of cycles must be a whole number greater than zero.";
    if (!PERIODS.includes(header.Period)) return "Select a valid recurring period.";
    if (!header.Particulars.trim()) return "Particulars are required.";
    if (options.bookingDate && dateOnly(header.DateStart) < dateOnly(options.bookingDate)) return `Start date cannot be earlier than the open period ${dateOnly(options.bookingDate)}.`;
    if (!lines.length) return "Add at least one recurring journal detail line.";
    for (let index = 0; index < lines.length; index += 1) {
      const line = lines[index];
      if (!line.AccountCode || !line.Location) return `Complete the account and branch for journal line ${index + 1}.`;
      if (line.Control && !line.Agent) return `Journal line ${index + 1} uses a control account and requires an agent.`;
      if (!Number.isFinite(Number(line.Debit)) || !Number.isFinite(Number(line.Credit))) return `Journal line ${index + 1} has an invalid debit or credit amount.`;
    }
    if (Math.abs(totals.debit) < 0.005 && Math.abs(totals.credit) < 0.005) return "Enter a debit and credit amount before saving.";
    if (Math.abs(variance) >= 0.01) return `The recurring journal entry is not balanced. Variance: ${money(variance)}.`;
    return "";
  };

  const save = async () => {
    const error = validate();
    if (error) return setNotice({ severity: "warning", text: error });
    try {
      setSaving(true);
      const data = await post("/api/recurring-jv/save", { compID, userID, mode, recid, header, details: lines });
      setHeader((current) => ({ ...current, JVCode: data.code, Status: data.status || current.Status }));
      setNotice({ severity: "success", text: data.message });
      setTimeout(() => navigate("/App/TLRecurringJV"), 800);
    } catch (saveError) {
      setNotice({ severity: "error", text: saveError.message });
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = editable ? { "& .MuiOutlinedInput-root": { bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(255,193,7,.10)" : "#fff8dc" } } : {};
  const titleMode = view ? "View" : mode === "edit" ? "Edit" : mode === "clone" ? "Clone" : "New";
  const centeredNotice = Boolean(notice && ["error", "warning"].includes(notice.severity));

  return <Box m={{ xs: 1.5, md: 3 }}>
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: "1px solid", borderColor: "divider", borderTop: "5px solid", borderTopColor: "primary.main", borderRadius: 4 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} alignItems={{ md: "center" }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ width: 48, height: 48, borderRadius: 2.5, bgcolor: "primary.main", color: "primary.contrastText", display: "grid", placeItems: "center" }}><EventRepeatOutlined /></Box>
          <Box><Typography variant="h3" fontWeight={900}>{titleMode} Recurring Journal Voucher</Typography><Typography color="text.secondary">{header.JVCode || "Enter a recurring code"} · {header.Status || "Pending"}</Typography></Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<ArrowBack />} disabled={saving} onClick={() => navigate("/App/TLRecurringJV")}>Back</Button>
          {editable && <Button variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlined />} disabled={saving || loading} onClick={save} sx={{ minWidth: 130 }}>{saving ? (mode === "edit" ? "Updating…" : "Saving…") : (mode === "edit" ? "Update" : "Save")}</Button>}
        </Stack>
      </Stack>
    </Paper>

    <Paper elevation={0} sx={{ mt: 2, border: "1px solid", borderColor: "divider", borderRadius: 4, overflow: "hidden" }}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="start" mb={2}>
          <Box><Typography variant="h5" fontWeight={800}>Recurring schedule</Typography><Typography color="text.secondary">Define when and how many Journal Vouchers will be generated.</Typography></Box>
          <Typography variant="caption" color="text.secondary">Yellow fields are editable · * Required</Typography>
        </Stack>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "220px minmax(260px,1fr) 190px 210px 210px" }, gap: 2 }}>
          <TextField label="Recurring JV Code *" value={header.JVCode || ""} onChange={(event) => setHeader({ ...header, JVCode: event.target.value })} disabled={!editable || mode === "edit"} inputProps={{ maxLength: 50 }} sx={fieldSx} />
          <TextField label="Description *" value={header.JVDescription || ""} onChange={(event) => setHeader({ ...header, JVDescription: event.target.value })} disabled={!editable} sx={fieldSx} />
          <TextField type="number" label="No. of Cycles *" value={header.Cycle ?? ""} onChange={(event) => setHeader({ ...header, Cycle: event.target.value })} disabled={!editable} inputProps={{ min: 1, step: 1 }} sx={fieldSx} />
          <TextField select label="Period *" value={header.Period || "Monthly"} onChange={(event) => setHeader({ ...header, Period: event.target.value })} disabled={!editable} sx={fieldSx}>{PERIODS.map((period) => <MenuItem key={period} value={period}>{period}</MenuItem>)}</TextField>
          <TextField type="date" label="Start Date *" value={header.DateStart || ""} onChange={(event) => setHeader({ ...header, DateStart: event.target.value })} disabled={!editable} InputLabelProps={{ shrink: true }} sx={fieldSx} />
        </Box>
        <TextField multiline minRows={3} fullWidth label="Particulars *" value={header.Particulars || ""} onChange={(event) => setHeader({ ...header, Particulars: event.target.value })} disabled={!editable} sx={{ ...fieldSx, mt: 2 }} />
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, bgcolor: (theme) => theme.palette.motif?.panel || "action.hover", borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography variant="h5" fontWeight={800}>Journal Entry Details</Typography>
        <Typography variant="body2" color="text.secondary">Only active Allow-JV accounts are available. Control accounts require an agent.</Typography>
        {editable && <Button variant="contained" startIcon={<AddCircleOutline />} onClick={() => setLines((current) => [...current, emptyLine(header.DefaultBranch)])} sx={{ mt: 1.5 }}>Add Journal Row</Button>}
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        <Box component="table" sx={{ width: "100%", minWidth: 1370, borderCollapse: "collapse", "& th, & td": { px: 1.25, py: 1, borderBottom: "1px solid", borderColor: "divider", verticalAlign: "middle" }, "& th": { bgcolor: (theme) => theme.palette.motif?.panel || "action.hover", textAlign: "left", fontFamily: "monospace", whiteSpace: "nowrap" } }}>
          <thead><tr><th>Actions</th><th>Account Code</th><th>Title</th><th>Control</th><th>Agent</th><th style={{ textAlign: "right" }}>Debit Amount</th><th style={{ textAlign: "right" }}>Credit Amount</th><th>Branch Location</th></tr></thead>
          <tbody>
            {lines.map((line, index) => <tr key={line._key || index}>
              <td><Stack direction="row" spacing={0.25}>
                <Tooltip title="Clone row"><span><IconButton size="small" color="primary" disabled={!editable} onClick={() => setLines((current) => [...current.slice(0, index + 1), { ...line, _key: lineKey() }, ...current.slice(index + 1)])}><ContentCopyOutlined fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Delete row"><span><IconButton size="small" color="error" disabled={!editable} onClick={() => setLines((current) => current.filter((_, lineIndex) => lineIndex !== index))}><DeleteOutline fontSize="small" /></IconButton></span></Tooltip>
              </Stack></td>
              <td><Autocomplete
                size="small"
                options={options.accounts}
                value={accountFor(line)}
                disabled={!editable}
                getOptionLabel={(account) => String(account.AccountCode || "")}
                isOptionEqualToValue={(a, b) => String(a.AccountCode) === String(b.AccountCode)}
                filterOptions={(accounts, state) => { const term = state.inputValue.trim().toLowerCase(); return !term ? accounts : accounts.filter((account) => `${account.AccountCode} ${account.AccountTitle || ""} ${account.AccountGroup || ""}`.toLowerCase().includes(term)); }}
                onChange={(_, account) => updateLine(index, { AccountCode: account?.AccountCode || "", Title: account?.AccountTitle || "", Control: Boolean(account?.Control_), Agent: "" })}
                renderOption={(optionProps, account) => <li {...optionProps} key={account.AccountCode}><Box><Typography fontWeight={800}>{account.AccountCode} · {account.AccountTitle}</Typography><Typography variant="caption" color="text.secondary">{account.AccountGroup || "General Ledger"}{account.Control_ ? " · Control account" : ""}</Typography></Box></li>}
                sx={{ ...fieldSx, minWidth: 180 }}
                renderInput={(inputParams) => <TextField {...inputParams} placeholder="Search code or title" />}
              /></td>
              <td><Typography sx={{ minWidth: 220 }}>{line.Title || accountFor(line)?.AccountTitle || ""}</Typography></td>
              <td><Checkbox checked={Boolean(line.Control)} disabled /></td>
              <td><Autocomplete
                size="small"
                options={options.agents}
                value={agentFor(line)}
                disabled={!editable || !line.Control}
                getOptionLabel={(agent) => `${agent.AgentCode} - ${agent.TradeName || agent.Name || ""}`}
                isOptionEqualToValue={(a, b) => String(a.AgentCode) === String(b.AgentCode)}
                filterOptions={(agents, state) => { const term = state.inputValue.trim().toLowerCase(); return !term ? agents : agents.filter((agent) => `${agent.AgentCode} ${agent.TradeName || ""} ${agent.Name || ""} ${agent.TIN || ""}`.toLowerCase().includes(term)); }}
                onChange={(_, agent) => updateLine(index, { Agent: agent?.AgentCode || "" })}
                sx={{ ...fieldSx, minWidth: 260 }}
                renderInput={(inputParams) => <TextField {...inputParams} placeholder={line.Control ? "Search agent" : "Not applicable"} error={Boolean(line.Control && !line.Agent)} />}
              /></td>
              {["Debit", "Credit"].map((field) => {
                const focusKey = `${index}-${field}`;
                return <td key={field}><TextField
                  size="small"
                  value={focusedAmount === focusKey ? String(line[field] ?? "") : money(line[field])}
                  disabled={!editable}
                  onFocus={(event) => { setFocusedAmount(focusKey); setTimeout(() => event.target.select(), 0); }}
                  onBlur={() => setFocusedAmount("")}
                  onChange={(event) => { const value = event.target.value.replace(/,/g, ""); if (amountPattern.test(value)) updateLine(index, { [field]: value }); }}
                  inputProps={{ inputMode: "decimal", style: { textAlign: "right" } }}
                  sx={{ ...fieldSx, width: 150 }}
                /></td>;
              })}
              <td><TextField select size="small" value={line.Location || ""} onChange={(event) => updateLine(index, { Location: event.target.value })} disabled={!editable} sx={{ ...fieldSx, minWidth: 180 }}>{options.branches.map((branch) => <MenuItem key={codeOf(branch)} value={codeOf(branch)}>{codeOf(branch)} - {branchName(branch)}</MenuItem>)}</TextField></td>
            </tr>)}
            {!lines.length && <tr><td colSpan={8}><Box py={6} textAlign="center"><Typography color="text.secondary">No journal lines added. Select Add Journal Row to begin.</Typography></Box></td></tr>}
          </tbody>
        </Box>
      </Box>

      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" spacing={{ xs: 1, sm: 4 }} sx={{ p: 2.5, bgcolor: (theme) => theme.palette.motif?.panel || "action.hover" }}>
        <Typography>Lines: <b>{lines.length}</b></Typography>
        <Typography>Total Debit: <b>{money(totals.debit)}</b></Typography>
        <Typography>Total Credit: <b>{money(totals.credit)}</b></Typography>
        <Typography color={Math.abs(variance) < 0.01 ? "success.main" : "error.main"}>Variance: <b>{money(variance)}</b></Typography>
      </Stack>
    </Paper>

    <Backdrop open={saving} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1, bgcolor: "rgba(0,0,0,.68)" }}>
      <Paper elevation={12} sx={{ p: 4, borderRadius: 3, minWidth: { xs: 280, sm: 420 }, textAlign: "center" }}>
        <CircularProgress size={56} />
        <Typography variant="h5" fontWeight={800} mt={2}>{mode === "edit" ? "Updating Recurring Journal Voucher" : "Saving Recurring Journal Voucher"}</Typography>
        <Typography color="text.secondary" mt={1}>Validating the recurring schedule and balanced journal lines. Please wait…</Typography>
      </Paper>
    </Backdrop>

    <Dialog open={centeredNotice} onClose={() => setNotice(null)} maxWidth="sm" fullWidth>
      <DialogTitle><Stack direction="row" spacing={1.5} alignItems="center"><ErrorOutline color={notice?.severity === "warning" ? "warning" : "error"} sx={{ fontSize: 34 }} /><Typography variant="h5" fontWeight={800}>{notice?.severity === "warning" ? "Please Review the Recurring JV" : "Unable to Complete the Action"}</Typography></Stack></DialogTitle>
      <DialogContent dividers><Alert severity={notice?.severity || "error"} variant="outlined">{notice?.text}</Alert><Typography color="text.secondary" mt={2}>Review the recurring schedule and journal details, then try again.</Typography></DialogContent>
      <DialogActions><Button variant="contained" onClick={() => setNotice(null)} autoFocus>OK, I Understand</Button></DialogActions>
    </Dialog>
    <Snackbar open={Boolean(notice && !centeredNotice)} autoHideDuration={5000} onClose={() => setNotice(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>{notice && !centeredNotice ? <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.text}</Alert> : undefined}</Snackbar>
  </Box>;
};

export default TransRecurringJV;
